import { Controller } from "stimulus"

// RaindancerDebugController
// Mostra in tempo reale nella pagina tutte le request/response verso le API Raindancer.
// Usato nella view manage_main_view per testare la comunicazione MQTT.
export default class extends Controller {
  static targets = ["log", "jsonInput", "speedInput", "preInput", "postInput", "datetimeStartInput", "datetimeEndInput"]

  connect() {
    this.lastProgram = null
    this.addLog('info', '=== Debug Panel pronto ===')
    this.addLog('info', `Serial: ${this.data.get('serial')} | Token: ${this.data.get('token')?.slice(0, 8)}...`)
  }

  // Invia un comando standard (start, stop, status, position, ecc.)
  sendCommand(event) {
    event.preventDefault()
    const el = event.currentTarget
    const command = el.dataset.command
    const extra = el.dataset.extra ? JSON.parse(el.dataset.extra) : {}

    const body = {
      serialNoRemote: this.data.get('serial'),
      version: "1.0.0",
      message: { command, ...extra }
    }

    // Params root-level aggiuntivi (es. status, error per stop)
    const rootExtra = el.dataset.rootExtra ? JSON.parse(el.dataset.rootExtra) : {}
    const finalBody = { ...body, ...rootExtra }

    // Se il comando è "start" e c'è un programma con datetime_start nel futuro,
    // invia prima un set_program aggiornato con l'orario attuale mantenendo la durata
    if (command === 'start' && this.lastProgram && this.lastProgram.datetime_start > Date.now()) {
      const duration = this.lastProgram.datetime_end - this.lastProgram.datetime_start
      const now = Date.now()
      const updatedProgram = {
        ...this.lastProgram,
        datetime_start: now,
        datetime_end: now + duration
      }
      this.lastProgram = updatedProgram
      const setProgramBody = {
        serialNoRemote: this.data.get('serial'),
        version: "1.0.0",
        message: { command: "set_program", ...updatedProgram }
      }
      this.addLog('info', `⏩ datetime_start nel futuro — invio set_program con orario attuale (durata: ${Math.round(duration / 60000)} min)`)
      this.execute(this.data.get('devices-endpoint'), setProgramBody).then(() => {
        this.execute(this.data.get('devices-endpoint'), finalBody)
      })
      return
    }

    this.execute(this.data.get('devices-endpoint'), finalBody)
  }

  // Invia setSpeed con il valore dall'input
  sendSetSpeed(event) {
    event.preventDefault()
    const speed = parseFloat(this.speedInputTarget.value)
    if (isNaN(speed) || speed <= 0) {
      this.addLog('error', '❌ Inserisci un valore di velocità valido')
      return
    }
    const body = {
      serialNoRemote: this.data.get('serial'),
      version: "1.0.0",
      message: { command: "setSpeed", value: speed }
    }
    this.execute(this.data.get('devices-endpoint'), body)
  }

  // Invia setPreIrrigation con il valore dall'input
  sendSetPreIrrigation(event) {
    event.preventDefault()
    const val = parseInt(this.preInputTarget.value)
    if (isNaN(val) || val < 0) {
      this.addLog('error', '❌ Inserisci un valore valido per pre-irrigazione')
      return
    }
    const body = {
      serialNoRemote: this.data.get('serial'),
      version: "1.0.0",
      message: { command: "setPreIrrigation", value: val }
    }
    this.execute(this.data.get('devices-endpoint'), body)
  }

  // Invia setPostIrrigation con il valore dall'input
  sendSetPostIrrigation(event) {
    event.preventDefault()
    const val = parseInt(this.postInputTarget.value)
    if (isNaN(val) || val < 0) {
      this.addLog('error', '❌ Inserisci un valore valido per post-irrigazione')
      return
    }
    const body = {
      serialNoRemote: this.data.get('serial'),
      version: "1.0.0",
      message: { command: "setPostIrrigation", value: val }
    }
    this.execute(this.data.get('devices-endpoint'), body)
  }

  // Crea una nuova sessione
  createSession(event) {
    event.preventDefault()
    const body = { serialNoRemote: this.data.get('serial'), machineUuid: this.data.get('machine-uuid') }
    this.execute(this.data.get('create-session-endpoint'), body)
  }

  // Termina la sessione corrente
  endSession(event) {
    event.preventDefault()
    const body = { serialNoRemote: this.data.get('serial'), machineUuid: this.data.get('machine-uuid') }
    this.execute(this.data.get('end-session-endpoint'), body)
  }

  // Converte un valore datetime-local (YYYY-MM-DDTHH:MM) in ms timestamp
  _datetimeLocalToMs(value) {
    if (!value) return null
    return new Date(value).getTime()
  }

  // Formatta un ms timestamp come stringa per un input datetime-local
  _msToDatetimeLocal(ms) {
    const d = new Date(ms)
    const pad = n => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
  }

  // Popola il textarea con un JSON di esempio per il comando set_program (UPD_PROG)
  fillSampleUpdProg(event) {
    event.preventDefault()
    const serial = event.currentTarget.dataset.serial
    const now = Date.now()
    const defaultStart = now + 5 * 60 * 1000
    const defaultEnd = now + 60 * 60 * 1000

    // Pre-popola gli input datetime se vuoti
    if (!this.datetimeStartInputTarget.value) {
      this.datetimeStartInputTarget.value = this._msToDatetimeLocal(defaultStart)
    }
    if (!this.datetimeEndInputTarget.value) {
      this.datetimeEndInputTarget.value = this._msToDatetimeLocal(defaultEnd)
    }

    const datetimeStart = this._datetimeLocalToMs(this.datetimeStartInputTarget.value) ?? defaultStart
    const datetimeEnd = this._datetimeLocalToMs(this.datetimeEndInputTarget.value) ?? defaultEnd

    const sample = {
      serialNoRemote: serial,
      version: "1.0.0",
      message: {
        command: "set_program",
        conf_work_mod: 0,
        work_mod: "m/h",
        datetime_start: datetimeStart,
        datetime_end: datetimeEnd,
        pause_start: 0,
        pause_end: 0,
        s1length: 120,
        s1speed: 40,
        s1height: null,
        mouth_pressure: 1,
        mouth_diameter: 6,
        bandwidth: 5,
        motormaticc_active: 0,
        motormaticc_pressure: 0,
        tube_length: 120,
        time_prevision: 7200,
        smart_irrigator: 0
      }
    }
    this.jsonInputTarget.value = JSON.stringify(sample, null, 2)
    this.addLog('info', '📋 Sample UPD_PROG caricato nel textarea')
  }

  // Invia il JSON custom dal textarea
  sendCustom(event) {
    event.preventDefault()
    try {
      const body = JSON.parse(this.jsonInputTarget.value)

      // Memorizza il programma se il comando è set_program
      if (body.message?.command === 'set_program') {
        const { command, ...programParams } = body.message
        this.lastProgram = programParams
        this.addLog('info', `💾 Programma memorizzato — start: ${new Date(programParams.datetime_start).toLocaleTimeString('it-IT')}, end: ${new Date(programParams.datetime_end).toLocaleTimeString('it-IT')}`)
      }

      this.execute(this.data.get('devices-endpoint'), body)
    } catch (e) {
      this.addLog('error', '❌ JSON non valido: ' + e.message)
    }
  }

  clearLog(event) {
    if (event) event.preventDefault()
    this.logTarget.innerHTML = ''
    this.addLog('info', '=== Log pulito ===')
  }

  // ––– Metodi privati –––

  execute(url, body) {
    const apiKey = this.data.get('api-key')
    const token = this.data.get('token')
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    this.addLog('separator', '─'.repeat(60))
    this.addLog('sent', `→ POST ${url}`)
    this.addLog('sent-body', JSON.stringify(body, null, 2))

    const formData = new FormData()
    this.flattenToFormData(formData, body, '')
    formData.append('Authorization', token)

    return fetch(url, {
      method: 'POST',
      headers: {
        'X-Api-Key': apiKey,
        'Authorization': `Bearer ${token}`,
        ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {})
      },
      body: formData
    })
      .then(res => {
        const status = res.status
        return res.text().then(text => {
          let parsed
          try { parsed = JSON.parse(text) } catch { parsed = text }
          return { status, body: parsed }
        })
      })
      .then(({ status, body }) => {
        const type = status >= 200 && status < 300 ? 'received' : 'error'
        this.addLog(type, `← RISPOSTA [${status}]`)
        this.addLog(type + '-body', typeof body === 'string' ? body : JSON.stringify(body, null, 2))

        // Ricarica la pagina per aggiornare lo stato sessione
        const shouldReload = url.includes('create_session') || url.includes('end_session') ||
          (typeof body === 'object' && (body.responseType === 'sessionCreated' || body.responseType === 'sessionEnded'))
        if (shouldReload) {
          setTimeout(() => this.refreshMain(), 800)
        }
      })
      .catch(err => {
        this.addLog('error', '❌ Errore fetch: ' + err.message)
      })
  }

  // Aggiorna via ajax solo la colonna principale (stato sessione, comandi), senza ricaricare la pagina.
  // Il log della console resta intatto; i valori di input/textarea vengono preservati.
  refreshMain() {
    return fetch(window.location.href, { headers: { 'X-Requested-With': 'XMLHttpRequest' }, credentials: 'same-origin' })
      .then(res => res.text())
      .then(html => {
        const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('.r-auth__main')
        const current = this.element.querySelector('.r-auth__main')
        if (fresh && current) {
          // Preserva i valori inseriti dall'utente (input/textarea con id) attraverso il refresh
          const saved = {}
          current.querySelectorAll('input[id], textarea[id]').forEach(el => { saved[el.id] = el.value })
          current.innerHTML = fresh.innerHTML
          Object.entries(saved).forEach(([id, value]) => {
            const el = current.querySelector(`#${id}`)
            if (el) el.value = value
          })
        } else window.location.reload()
      })
      .catch(err => this.addLog('error', '❌ Errore refresh: ' + err.message))
  }

  flattenToFormData(fd, obj, prefix) {
    Object.keys(obj).forEach(key => {
      const name = prefix ? `${prefix}[${key}]` : key
      const val = obj[key]
      if (val !== null && val !== undefined && typeof val === 'object') {
        this.flattenToFormData(fd, val, name)
      } else if (val !== null && val !== undefined) {
        fd.append(name, String(val))
      }
    })
  }

  addLog(type, message) {
    const log = this.logTarget
    const now = new Date()
    const timestamp = now.toLocaleTimeString('it-IT', {
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    }) + '.' + String(now.getMilliseconds()).padStart(3, '0')

    const div = document.createElement('div')
    div.className = `rd-log rd-log--${type}`

    const colors = {
      info:           '#888',
      separator:      '#444',
      sent:           '#61afef',
      'sent-body':    '#98c379',
      received:       '#e5c07b',
      'received-body':'#56b6c2',
      error:          '#e06c75',
      'error-body':   '#e06c75',
    }

    div.style.cssText = [
      `color: ${colors[type] || '#abb2bf'}`,
      'font-family: monospace',
      'font-size: 12px',
      'line-height: 1.5',
      type.endsWith('-body') ? 'white-space: pre; padding-left: 16px;' : '',
      type === 'separator' ? 'margin: 4px 0;' : '',
    ].join(';')

    div.textContent = type === 'separator' ? message : `[${timestamp}] ${message}`
    log.appendChild(div)
    log.scrollTop = log.scrollHeight
  }
}
